class webRtcWorker {
    constructor(params) {
        this.messageObject = params.messageObject;
        this._channelID = params.channelID;
        this._myRegisteredName = params.myRegisteredName;
        this._callType = params.callType;
        this.localStream = params.localStream;
        this.sendingLocalVideo = params.sendingLocalVideo;
        this.sendingLocalAudio = params.sendingLocalAudio;

        this._hangupRequested = false;
        this.remoteStream = null;
        this._audio_codec = null;
        this._sequence = 0;
        this.callPeer = "";
        this.initiator = false;
        this.peerConnectionCreated = false;
        this.negotiating = false;
        this.connectionEstablished = false;
        this.pc = null;
        this.statsIntervalID = 0;
        this._disconnectTimerID = null;
        this.lastInboundStats = null;
        this.lastOutboundStats = null;
        this.lastSelectedCandidatePairId = null;
        this.lastRouteBytesSent = 0;
        this.lastRouteBytesReceived = 0;
        this.turnBytesSent = 0;
        this.turnBytesReceived = 0;
        this.pcConfig = params.pcConfig || { iceServers: [] };
        this.handleCreateSessionDescriptionError = this.handleCreateSessionDescriptionError.bind(this);
        this.init();
    }

    set myRegisteredName(myRegisteredName) {
        this._myRegisteredName = myRegisteredName;
        if (this._myRegisteredName === this.messageObject.callInitiator) {
            this.callPeer = this.messageObject.callReceiver;
            this.initiator = true;
        } else {
            this.callPeer = this.messageObject.callInitiator;
            this.initiator = false;
        }
    }

    set callType(callType) { this._callType = callType; }
    set hangupRequested(hangupRequested) { this._hangupRequested = hangupRequested; }
    set audio_codec(audio_codec) { this._audio_codec = audio_codec; }
    set channelID(channelID) { this._channelID = channelID; }

    handshakeReceived(messageObject) {
        if (messageObject.webrtc.type === 'offer' &&
            messageObject.callReceiver === this._myRegisteredName) {
            if (!this.initiator && !this.peerConnectionCreated) {
                this.createPeerConnection();
                if (this.sendingLocalVideo) this.addTrackToSend(this.localStream, 'video');
                if (this.sendingLocalAudio) this.addTrackToSend(this.localStream, 'audio');
            }
            this.createAnswer(messageObject.webrtc);
        } else if (messageObject.webrtc.type === 'answer' &&
                   this.peerConnectionCreated &&
                   messageObject.callInitiator === this._myRegisteredName) {
            this.setRemoteDescription(messageObject.webrtc);
        } else if (messageObject.webrtc.type === 'candidate' &&
                   this.peerConnectionCreated &&
                   messageObject.callReceiver === this._myRegisteredName) {
            this.addIceCandidate(messageObject.webrtc);
        }
    }

    createPeerConnection() {
        try {
            this.pc = new RTCPeerConnection(this.pcConfig);
            this.pc.onicecandidate = this.handleIceCandidate.bind(this);
            this.pc.ontrack = this.handleRemoteTrackAdded.bind(this);
            this.pc.oniceconnectionstatechange = this.handleIceConnectionStateChange.bind(this);
            this.pc.onnegotiationneeded = this.handleNegotiationNeeded.bind(this);
            this.pc.onsignalingstatechange = this.handleSignallingStateChange.bind(this);
            this.peerConnectionCreated = true;
        } catch (e) {
            this.trigger("callFailed", this._channelID, e.message);
            console.log('Failed to create PeerConnection: ' + e.message);
        }
    }

    close() {
        clearTimeout(this._disconnectTimerID);
        this._disconnectTimerID = null;
        if (this.pc) {
            this.pc.close();
            this.pc = null;
        }
        this.peerConnectionCreated = false;
        this.connectionEstablished = false;
        clearInterval(this.statsIntervalID);
    }

    addTrackToSend(stream, trackType) {
        if (trackType === 'audio') {
            this.pc.addTrack(stream.getAudioTracks()[0], stream);
        } else {
            this.pc.addTrack(stream.getVideoTracks()[0], stream);
        }
    }

    createAnswer(sessionDescription) {
        console.log('signalingState', this.pc.signalingState);
        if (this.pc.signalingState === 'have-local-offer') {
            // glare condition: both sides sent an offer simultaneously; exit
            return;
        }
        this.pc.setRemoteDescription(sessionDescription)
            .then(() => this.pc.createAnswer())
            .then(answer => this.setLocalAndSendMessage(answer))
            .catch(e => this.handleCreateSessionDescriptionError(e));
    }

    setRemoteDescription(sessionDescription) {
        this.pc.setRemoteDescription(sessionDescription)
            .catch(err => this.trigger("callFailed", this._channelID, 'setRemoteDescription: ' + err.message));
    }

    addIceCandidate(sessionDescription) {
        const candidate = new RTCIceCandidate({
            sdpMLineIndex: sessionDescription.label,
            candidate: sessionDescription.candidate
        });
        this.pc.addIceCandidate(candidate)
            .catch(err => console.warn('addIceCandidate failed: ' + err.message));
    }

    replaceTracks(newSourceTracks) {
        let newAudioTrack;
        let newVideoTrack;
        for (let x = 0; x < newSourceTracks.length; x++) {
            if (newSourceTracks[x].kind === 'video') {
                newVideoTrack = newSourceTracks[x];
            } else {
                newAudioTrack = newSourceTracks[x];
            }
        }

        const senders = this.pc.getSenders();
        for (let x = 0; x < senders.length; x++) {
            if (!senders[x].track || typeof senders[x].track.kind === 'undefined') {
                console.log('ERROR - cannot find attribute kind on senders track', senders[x].track);
                continue;
            }
            if (senders[x].track.kind === 'video') {
                senders[x].replaceTrack(newVideoTrack)
                    .catch(err => this.trigger("replaceTracks", this.callPeer, 'replace failed ' + err));
            } else {
                senders[x].replaceTrack(newAudioTrack)
                    .catch(err => this.trigger("replaceTracks", this.callPeer, 'replace failed ' + err));
            }
        }
    }

    handleIceCandidate(event) {
        this.sendStateDebugInfo('handleIceCandidate');
        if (event.candidate) {
            const newMessage = { ...this.messageObject };
            newMessage.action = 'webrtc';
            newMessage.webrtc = {
                type: 'candidate',
                label: event.candidate.sdpMLineIndex,
                id: event.candidate.sdpMid,
                candidate: event.candidate.candidate
            };
            newMessage.callInitiator = this._myRegisteredName;
            newMessage.callReceiver = this.callPeer;
            this.trigger("sendMessage", newMessage);
        }
        if (this.pc.iceGatheringState === 'complete' && !this.connectionEstablished) {
            this.connectionEstablished = true;
            this.trigger("connectionSuccessful", this.callPeer);
        }
    }

    handleRemoteTrackAdded(event) {
        console.log('remote connection success', event);
        this.sendStateDebugInfo('handleRemoteTrackAdded');
        if (this.remoteStream === event.streams[0] || typeof event.streams[0] === 'undefined') {
            return;
        }
        this.remoteStream = event.streams[0];
        this.trigger("remoteStreamObtained", this._channelID, event.streams[0]);

        const messageObject = { ...this.messageObject };
        messageObject.action = 'remoteConnectionSuccess';
        this.trigger("sendMessage", messageObject);

        this.statsIntervalID = setInterval(this.getStats.bind(this), 1000);
    }

    handleIceConnectionStateChange(event) {
        if (this.pc === null) return;
        this.sendStateDebugInfo('handleIceConnectionStateChange');

        if (this.pc.iceGatheringState === 'complete' && !this.connectionEstablished) {
            this.connectionEstablished = true;
            this.trigger("connectionSuccessful", this.callPeer);
        }

        if (this.pc.iceConnectionState === 'disconnected' && !this._hangupRequested) {
            // only the initiator restarts ICE to avoid both sides creating offers simultaneously
            if (this.initiator) {
                const options = { iceRestart: true, offerToReceiveVideo: true, offerToReceiveAudio: true };
                this.pc.createOffer(options)
                    .then(sd => this.setLocalAndSendMessage(sd))
                    .catch(err => this.handleCreateOfferError(err));
            }
            // Fallback: browsers often stay in 'disconnected' indefinitely (never reach 'failed')
            // so declare the call dead after 6 seconds if not recovered
            clearTimeout(this._disconnectTimerID);
            this._disconnectTimerID = setTimeout(() => {
                if (this.pc &&
                    (this.pc.iceConnectionState === 'disconnected' || this.pc.iceConnectionState === 'failed') &&
                    !this._hangupRequested) {
                    this.trigger("callFailed", this._channelID, 'disconnectTimeout');
                }
            }, 6000);
        } else if (this.pc.iceConnectionState === 'connected' || this.pc.iceConnectionState === 'completed') {
            // Connection recovered — cancel the disconnect timer
            clearTimeout(this._disconnectTimerID);
            this._disconnectTimerID = null;
        } else if (this.pc.iceConnectionState === 'failed' && !this._hangupRequested) {
            clearTimeout(this._disconnectTimerID);
            this._disconnectTimerID = null;
            this.trigger("callFailed", this._channelID, 'handleIceConnectionStateChange');
        }
    }

    setLocalAndSendMessage(sessionDescription) {
        if (this._audio_codec !== null) {
            sessionDescription.sdp = this.setPreferredCodec(sessionDescription.sdp, "audio", this._audio_codec);
        }
        this.pc.setLocalDescription(sessionDescription)
            .then(() => {
                const messageObject = { ...this.messageObject };
                messageObject.action = 'webrtc';
                messageObject.webrtc = sessionDescription;
                this.trigger("sendMessage", messageObject);
            })
            .catch(err => this.handleCreateSessionDescriptionError(err));
    }

    setPreferredCodec(sdp, type, codec) {
        const sdpLines = sdp.split('\r\n');
        const codecNumbers = [];
        let codecPreferenceIndex = 0;
        for (let i = 0; i < sdpLines.length; i++) {
            if (sdpLines[i].indexOf('a=rtpmap') === 0) {
                if (sdpLines[i].toLowerCase().indexOf(codec.toLowerCase()) !== -1) {
                    codecNumbers.push(sdpLines[i].substring(9, sdpLines[i].indexOf(' ')));
                }
            } else if (sdpLines[i].indexOf('m=' + type) === 0) {
                codecPreferenceIndex = i;
            }
        }
        if (codecNumbers.length === 0) return sdp;

        const elements = sdpLines[codecPreferenceIndex].split(' ');
        const newLine = elements.slice(0, 3);
        for (let i = 0; i < codecNumbers.length; i++) newLine.push(codecNumbers[i]);
        for (let i = 3; i < elements.length; i++) {
            if (!codecNumbers.includes(elements[i])) newLine.push(elements[i]);
        }
        sdpLines[codecPreferenceIndex] = newLine.join(' ');
        return sdpLines.join('\r\n');
    }

    handleCreateOfferError(error) {
        console.log('handleCreateOfferError', error);
        this.sendStateDebugInfo('handleCreateOfferError');
        this.trigger("callFailed", this._channelID, 'handleCreateOfferError');
    }

    handleCreateSessionDescriptionError(error) {
        console.log('handleCreateSessionDescriptionError', error);
        this.sendStateDebugInfo('handleCreateSessionDescriptionError');
        this.trigger("callFailed", this._channelID, 'handleCreateSessionDescriptionError');
    }

    handleNegotiationNeeded(event) {
        this.sendStateDebugInfo('handleNegotiationNeeded');
        if (this.initiator && !this.negotiating) {
            this.negotiating = true;
            const options = { offerToReceiveVideo: true, offerToReceiveAudio: true };
            this.pc.createOffer(options)
                .then(sd => this.setLocalAndSendMessage(sd))
                .catch(err => this.handleCreateOfferError(err));
        }
    }

    handleSignallingStateChange(event) {
        if (!this.pc) return;
        this.sendStateDebugInfo('handleSignallingStateChange');
        if (this.initiator) {
            this.negotiating = (this.pc.signalingState !== "stable");
        }
    }

    sendStateDebugInfo(sourceMethod) {
        if (!this.pc) return;
        const timeStamp = new Date();
        const debugObject = {
            uniqueKey: this._channelID + "/" + this._sequence.toString().padStart(5, "0"),
            channelID: this._channelID,
            timestamp: timeStamp.getTime(),
            sequence: this._sequence,
            sourceMethod,
            connectionState: this.pc.connectionState,
            iceConnectionState: this.pc.iceConnectionState,
            iceGatheringState: this.pc.iceGatheringState,
            peerIdentity: this.pc.peerIdentity,
            sctp: this.pc.sctp,
            signalingState: this.pc.signalingState
        };
        this.trigger("sendStateDebugInfo", this._channelID, debugObject);
        this._sequence++;
    }

    getStats() {
        const reportObject = {
            iceRoute: 'unknown',
            localCandidateType: 'unknown',
            remoteCandidateType: 'unknown',
            turnBytesSent: this.turnBytesSent,
            turnBytesReceived: this.turnBytesReceived,
            turnMbTotal: (this.turnBytesSent + this.turnBytesReceived) / 1048576,
        };
        this.pc.getStats(null)
            .then(stats => {
                let selectedCandidatePair = null;
                stats.forEach(statsObject => {
                    if (statsObject.type === 'transport' && statsObject.selectedCandidatePairId) {
                        selectedCandidatePair = stats.get(statsObject.selectedCandidatePairId);
                    } else if (statsObject.type === 'candidate-pair' &&
                               (statsObject.selected || statsObject.nominated)) {
                        selectedCandidatePair = statsObject;
                    }

                    if (statsObject.type === 'inbound-rtp' &&
                        (statsObject.kind === 'audio' || statsObject.mediaType === 'audio')) {
                        reportObject.timestampIn = statsObject.timestamp;
                        reportObject.bytesReceived = statsObject.bytesReceived;
                        reportObject.packetsReceived = statsObject.packetsReceived;
                        reportObject.jitter = statsObject.jitter;
                        // audioLevel moved from deprecated 'track' stats to inbound-rtp in modern browsers
                        if (statsObject.audioLevel != null) reportObject.audioLevel = statsObject.audioLevel;
                        reportObject.bitrateIn = 0;
                        reportObject.packetrateIn = 0;
                        if (this.lastInboundStats !== null && statsObject.id === this.lastInboundStats.id) {
                            reportObject.bitrateIn =
                                8 * (reportObject.bytesReceived - this.lastInboundStats.bytesReceived) /
                                (reportObject.timestampIn - this.lastInboundStats.timestamp);
                            reportObject.packetrateIn = reportObject.packetsReceived - this.lastInboundStats.packetsReceived;
                        }
                        this.lastInboundStats = statsObject;

                    } else if (statsObject.type === 'outbound-rtp') {
                        reportObject.timestampOut = statsObject.timestamp;
                        reportObject.bytesSent = statsObject.bytesSent;
                        reportObject.packetsSent = statsObject.packetsSent;
                        reportObject.bitrateOut = 0;
                        reportObject.packetrateOut = 0;
                        if (this.lastOutboundStats !== null && statsObject.id === this.lastOutboundStats.id) {
                            reportObject.bitrateOut =
                                8 * (reportObject.bytesSent - this.lastOutboundStats.bytesSent) /
                                (reportObject.timestampOut - this.lastOutboundStats.timestamp);
                            reportObject.packetrateOut = reportObject.packetsSent - this.lastOutboundStats.packetsSent;
                        }
                        this.lastOutboundStats = statsObject;
                    }
                });
                if (selectedCandidatePair) {
                    const localCandidate = stats.get(selectedCandidatePair.localCandidateId);
                    const remoteCandidate = stats.get(selectedCandidatePair.remoteCandidateId);
                    const localType = localCandidate?.candidateType || 'unknown';
                    const remoteType = remoteCandidate?.candidateType || 'unknown';
                    const isTurn = localType === 'relay' || remoteType === 'relay';

                    reportObject.localCandidateType = localType;
                    reportObject.remoteCandidateType = remoteType;
                    reportObject.iceRoute = isTurn ? 'turn' : 'direct';

                    if (this.lastSelectedCandidatePairId !== selectedCandidatePair.id) {
                        this.lastSelectedCandidatePairId = selectedCandidatePair.id;
                        this.lastRouteBytesSent = reportObject.bytesSent || 0;
                        this.lastRouteBytesReceived = reportObject.bytesReceived || 0;
                    }
                    if (isTurn) {
                        const bytesSent = reportObject.bytesSent || 0;
                        const bytesReceived = reportObject.bytesReceived || 0;
                        this.turnBytesSent += Math.max(0, bytesSent - this.lastRouteBytesSent);
                        this.turnBytesReceived += Math.max(0, bytesReceived - this.lastRouteBytesReceived);
                    }
                    this.lastRouteBytesSent = reportObject.bytesSent || this.lastRouteBytesSent;
                    this.lastRouteBytesReceived = reportObject.bytesReceived || this.lastRouteBytesReceived;
                    reportObject.turnBytesSent = this.turnBytesSent;
                    reportObject.turnBytesReceived = this.turnBytesReceived;
                    reportObject.turnMbTotal = (this.turnBytesSent + this.turnBytesReceived) / 1048576;
                }
                this.trigger("stats", this._channelID, reportObject);
            });
    }

    init() {
        if (this._myRegisteredName === this.messageObject.callInitiator) {
            this.callPeer = this.messageObject.callReceiver;
            this.initiator = true;
            this.createPeerConnection();
            if (this.sendingLocalVideo || this.sendingLocalAudio) {
                this.localStream.getTracks().forEach(track => {
                    this.pc.addTrack(track, this.localStream);
                });
            }
        } else {
            this.callPeer = this.messageObject.callInitiator;
            this.initiator = false;
        }
    }
}

export default webRtcWorker;
